import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Heart, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/payments`;
const REFRESH_MS = 120000; // refetch donation feed every 2 minutes
const ROTATE_MS = 9000;    // rotate toast every 9s
const FIRST_DELAY_MS = 4000; // wait 4s after page load before first toast

const timeAgo = (iso) => {
  if (!iso) return 'just now';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'just now';
  const diff = Math.max(0, Date.now() - then) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  if (diff < 86400 * 14) return `${Math.floor(diff / 86400)} d ago`;
  return new Date(iso).toLocaleDateString();
};

const formatAmount = (amount, currency) => {
  const n = Number(amount || 0);
  return `${currency || 'MYR'} ${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const initialOf = (name) => {
  if (!name) return 'A';
  const c = name.trim()[0];
  return c ? c.toUpperCase() : 'A';
};

export const SocialProofToast = () => {
  const [donations, setDonations] = useState([]);
  const [index, setIndex] = useState(0);
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => typeof window !== 'undefined' && sessionStorage.getItem('ewrf_proof_dismissed') === '1'
  );

  useEffect(() => {
    if (dismissed) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/recent`, { params: { limit: 8 } });
        if (!cancelled) setDonations(res.data?.donations || []);
      } catch {
        // ignore
      }
    };
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [dismissed]);

  // Reveal + rotate
  useEffect(() => {
    if (dismissed || donations.length === 0) return;
    const showTimer = setTimeout(() => setShow(true), FIRST_DELAY_MS);
    const rotate = setInterval(() => {
      setShow(false);
      setTimeout(() => {
        setIndex((i) => (i + 1) % donations.length);
        setShow(true);
      }, 400);
    }, ROTATE_MS);
    return () => {
      clearTimeout(showTimer);
      clearInterval(rotate);
    };
  }, [donations.length, dismissed]);

  const current = useMemo(
    () => (donations.length ? donations[index % donations.length] : null),
    [donations, index]
  );

  if (dismissed || !current) return null;

  return (
    <div
      data-testid="social-proof-toast"
      className={`fixed left-4 md:left-8 bottom-24 md:bottom-28 z-40 transition-all duration-500 ${
        show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3 pointer-events-none'
      }`}
    >
      <div className="flex items-center gap-3 bg-white/95 backdrop-blur-md border border-gray-200 shadow-2xl rounded-2xl pl-2 pr-3 py-2 max-w-[320px]">
        <div className="relative flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[#FF6B6B] to-[#EE5A5A] text-white flex items-center justify-center font-black">
          {initialOf(current.name)}
          <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-emerald-500 border-2 border-white flex items-center justify-center">
            <Heart className="w-3 h-3 fill-white text-white" />
          </span>
        </div>
        <div className="flex-1 min-w-0 text-left">
          <div className="text-[13px] leading-tight text-gray-900">
            <span className="font-bold" data-testid="social-proof-name">{current.name}</span>{' '}
            <span className="text-gray-600">just donated</span>{' '}
            <span className="font-black text-[#1565C0]" data-testid="social-proof-amount">
              {formatAmount(current.amount, current.currency)}
            </span>
            {current.recurring && (
              <span className="ml-1 text-[10px] uppercase tracking-wider font-bold text-emerald-600">
                /mo
              </span>
            )}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mt-0.5">
            {timeAgo(current.at)} · verified
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            sessionStorage.setItem('ewrf_proof_dismissed', '1');
            setDismissed(true);
          }}
          data-testid="social-proof-dismiss"
          className="flex-shrink-0 w-6 h-6 -mr-1 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 flex items-center justify-center"
          aria-label="Dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default SocialProofToast;
